import { createAction, createSlice } from '@reduxjs/toolkit';
import { RootState } from '.';
import { getQueryParamsFromURL, sessionKeys } from 'src/helpers/domain.helper';
import { getDataFromSessionStorage, setDataInSessionStorage } from 'src/helpers/session-storage.helper';
import { SS_QUERY_PARAMS } from 'src/constants/constants';
import { omit } from 'lodash-es';
import { updateQueryParamsInCurrentLocation } from 'src/helpers/url.helper';

export const DEEPLINK_KEYS = ['view', 'action'];

type TInitialState = {
  queryParams?: Record<string, any> | null;
};

const handleRoute = createAction('handleRoute', () => {
  const params = getQueryParamsFromURL();
  if (params && Object.keys(params).length > 0) {
    const filteredParams = omit(params, sessionKeys);
    const fromSession = getDataFromSessionStorage(SS_QUERY_PARAMS) || {};
    const newParams = { ...fromSession, ...filteredParams };
    setDataInSessionStorage(SS_QUERY_PARAMS, newParams);
    return {
      payload: newParams,
    };
  }
  return {
    payload: getDataFromSessionStorage(SS_QUERY_PARAMS) || {},
  };
});

// Action to clear deep link keys (DEEPLINK_KEYS)
const clearDeeplinkQueryParams = createAction('clearDeeplinkQueryParams', (keysToBeRemoved = DEEPLINK_KEYS) => {
  const sessionParams = getDataFromSessionStorage(SS_QUERY_PARAMS) || {};
  const remainingParams = omit(sessionParams, keysToBeRemoved); // Remove specified keys from session
  setDataInSessionStorage(SS_QUERY_PARAMS, remainingParams);
  return {
    payload: remainingParams,
  };
});

// Action to clear passed keys from query params
const clearKeysFromQueryParams = createAction('clearKeysFromQueryParams', (keysToBeRemoved: Array<string>) => {
  const sessionParams = getDataFromSessionStorage(SS_QUERY_PARAMS) || {};
  const remainingParams = omit(sessionParams, keysToBeRemoved); // Remove specified keys from session
  setDataInSessionStorage(SS_QUERY_PARAMS, remainingParams);
  return {
    payload: remainingParams,
  };
});

// Action to add passed keys to query params in the store and the URL
const addQueryParamsInCurrentUrlAndStore = createAction(
  'addQueryParamsInCurrentUrlAndStore',
  (keysToAdd: Record<string, any>) => {
    const currentParams = getDataFromSessionStorage(SS_QUERY_PARAMS) || {};
    const newParams = { ...currentParams, ...keysToAdd };
    setDataInSessionStorage(SS_QUERY_PARAMS, newParams);
    updateQueryParamsInCurrentLocation(newParams);
    return {
      payload: newParams,
    };
  },
);

const initialState: TInitialState = {};

const routeSlice = createSlice({
  name: 'routeSlice',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(handleRoute, (state, action) => {
      state.queryParams = { ...state.queryParams, ...action.payload };
    });
    builder.addCase(clearDeeplinkQueryParams, (state, action) => {
      state.queryParams = action.payload;
    });
    builder.addCase(clearKeysFromQueryParams, (state, action) => {
      state.queryParams = action.payload;
    });
    builder.addCase(addQueryParamsInCurrentUrlAndStore, (state, action) => {
      state.queryParams = action.payload;
    });
  },
});

export { handleRoute, clearDeeplinkQueryParams, clearKeysFromQueryParams, addQueryParamsInCurrentUrlAndStore };

export const routeSelector = ({ route }: RootState) => route.queryParams || {};

export const getSourceSelector = ({ route }: RootState) => route?.queryParams?.source || '';

export default routeSlice.reducer;
